<template>
    <v-overlay
        v-model="isVisible"
        @update:modelValue="(val) => emit('update:modelValue', val)"
        @click:outside="emit('update:modelValue', false)"
        class="login-overlay"
        persistent
        scrim="#000000aa"
        z-index="9999"
    >
        <v-container class="login-content">
            <v-card class="login-card" max-width="500" elevation="10" rounded="xl">
                <div v-if="!confirmationStep" class="login-inner pa-4">
                    <div class="login-inner pa-4">
                        <InfoBox :infoMessage="info_message" class="mb-4" />

                        <v-btn
                            block
                            class="mb-3"
                            height="44"
                            style="
                                background-color: white;
                                color: #444;
                                font-weight: 500;
                                border: 1px solid #ccc;
                            "
                            @click="triggerGoogleLogin"
                        >
                            <v-icon left size="20">mdi-google</v-icon>
                            Sign in with Google
                        </v-btn>

                        <v-btn
                            block
                            class="mb-3"
                            style="background-color: #5865f2; color: white"
                            @click="handleDiscordLogin"
                        >
                            <v-icon left>mdi-discord</v-icon>
                            Login with Discord
                        </v-btn>

                        <div class="divider my-4">OR</div>

                        <v-btn
                            variant="text"
                            block
                            color="primary"
                            @click="showManual = !showManual"
                        >
                            {{ showManual ? 'Hide' : 'Email or phone number' }}
                        </v-btn>

                        <v-expand-transition>
                            <div v-show="showManual">
                                <v-row justify="center" class="my-3" v-if="!showForgotPassword">
                                    <h3 class="mr-4" :class="{ 'duelly-primary': !isLogin }">
                                        Signup
                                    </h3>
                                    <v-switch v-model="isLogin" inset color="primary" />
                                    <h3 class="ml-4" :class="{ 'duelly-primary': isLogin }">
                                        Login
                                    </h3>
                                </v-row>

                                <!-- Normal Login / Signup -->
                                <v-form
                                    v-if="!showForgotPassword"
                                    @submit.prevent="isLogin ? handleLogin() : handleSignup()"
                                >
                                    <v-text-field
                                        v-model="formData.contact"
                                        label="Email or XXX-XXX-XXXX"
                                        type="text"
                                        :loading="loading"
                                        required
                                        class="custom-label"
                                    />
                                    <v-text-field
                                        v-model="formData.password"
                                        label="Password"
                                        :type="showPassword ? 'text' : 'password'"
                                        :loading="loading"
                                        required
                                        append-icon="mdi-eye"
                                        @click:append="showPassword = !showPassword"
                                    />

                                    <div v-if="isLogin" class="d-flex justify-end mt-1">
                                        <v-btn
                                            variant="text"
                                            color="primary"
                                            class="pa-0 text-caption"
                                            @click.prevent="showForgotPassword = 'request'"
                                        >
                                            Forgot password?
                                        </v-btn>
                                    </div>

                                    <v-alert
                                        v-if="errorMessage"
                                        :text="errorMessage"
                                        type="error"
                                        variant="tonal"
                                        style="border-radius: 8px; padding: 16px; color: #d32f2f"
                                    />

                                    <v-btn color="primary" type="submit" class="mt-4" block>
                                        {{ isLogin ? 'Login' : 'Sign Up' }}
                                    </v-btn>
                                </v-form>

                                <!-- Forgot Password Step 1 -->
                                <div v-else-if="showForgotPassword === 'request'">
                                    <v-text-field
                                        v-model="resetContact"
                                        label="Email or phone"
                                        type="text"
                                        :loading="loading"
                                        required
                                    />
                                    <v-btn
                                        block
                                        color="primary"
                                        class="mt-2"
                                        @click="handleResetRequest"
                                    >
                                        Send Reset Code
                                    </v-btn>
                                    <v-btn
                                        variant="text"
                                        block
                                        class="mt-1"
                                        @click="showForgotPassword = false"
                                    >
                                        Back to Login
                                    </v-btn>
                                </div>

                                <!-- Forgot Password Step 2 -->
                                <div v-else-if="showForgotPassword === 'code'">
                                    <v-text-field
                                        v-model="resetCode"
                                        label="Enter Code"
                                        maxlength="6"
                                    />
                                    <v-text-field
                                        v-model="newPassword"
                                        label="New Password"
                                        type="password"
                                    />
                                    <v-text-field
                                        v-model="confirmPassword"
                                        label="Confirm New Password"
                                        :type="showPassword ? 'text' : 'password'"
                                        :error="confirmPassword && confirmPassword !== newPassword"
                                        :error-messages="
                                            confirmPassword && confirmPassword !== newPassword
                                                ? ['Passwords do not match']
                                                : []
                                        "
                                        required
                                    />
                                    <v-btn
                                        block
                                        color="primary"
                                        class="mt-2"
                                        @click="handleResetConfirm"
                                    >
                                        Confirm Reset
                                    </v-btn>
                                    <v-btn
                                        variant="text"
                                        block
                                        class="mt-1"
                                        @click="showForgotPassword = false"
                                    >
                                        Back to Login
                                    </v-btn>
                                </div>
                            </div>
                        </v-expand-transition>
                    </div>
                </div>

                <div v-else class="pa-4">
                    <p class="text-center mb-4 text-h6 duelly-fog">
                        A confirmation code was sent to {{ confirmationContact }}
                    </p>

                    <v-text-field
                        v-model="confirmationCode"
                        label="Enter Confirmation Code"
                        autofocus
                        :loading="loading"
                        @input="handleCodeInput"
                    />

                    <v-alert
                        v-if="errorMessage"
                        :text="errorMessage"
                        type="error"
                        variant="tonal"
                        style="border-radius: 8px; padding: 16px; color: #d32f2f"
                    />

                    <v-btn color="primary" block class="mt-4" @click="handleConfirmation">
                        Confirm and Login
                    </v-btn>
                </div>
            </v-card>
        </v-container>
    </v-overlay>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import InfoBox from '@/components/InfoBox.vue';
import { duellyEventEmitter } from '@/services/eventBus';
import UserService from '@/services/UserService';
import { useAuthStore } from '@/store/authStore';
import { useChallengeStore } from '@/store/challengeStore';
import { useLikeStore } from '@/store/likeStore';
import { useResponseStore } from '@/store/responseStore';

const isVisible = ref(false); // v-model for v-overlay
const showManual = ref(false);

const router = useRouter();
const auth_store = useAuthStore();
const challenge_store = useChallengeStore();
const like_store = useLikeStore();
const response_store = useResponseStore();

challenge_store.clearCache();
like_store.clearLikes();
response_store.clear();

const props = defineProps({
    modelValue: Boolean,
});
const emit = defineEmits(['update:modelValue']);

watch(
    () => props.modelValue,
    (val) => {
        isVisible.value = val;
    }
);

const info_message = ref('');
const loading = ref(undefined);
const showPassword = ref(false);
const errorMessage = ref('');
const isLogin = ref(true);
const googleButtonInitialized = ref(false);
const confirmationStep = ref(false);
const confirmationCode = ref('');
const confirmationContact = ref('');
const confirmationUserId = ref('');
const showForgotPassword = ref(false);
const resetContact = ref('');
const resetCode = ref('');
const newPassword = ref('');
const confirmPassword = ref('');

const formData = ref({
    contact: '',
    password: '',
});

function reset() {
    formData.value = { contact: '', password: '' };
    showManual.value = false;
    isLogin.value = true;
    showPassword.value = false;
    errorMessage.value = '';
    info_message.value = '';
    loading.value = undefined;
    confirmationStep.value = false;
    confirmationCode.value = '';
    confirmationContact.value = '';
    confirmationUserId.value = '';

    // clear redirect
    localStorage.removeItem('redirectAfterLogin');
}

async function handleLogin() {
    console.log('[LoginOverlay] handleLogin');
    loading.value = '#FF5C39';

    const { contact, password } = formData.value;
    let loginContact = contact;

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^\+?[0-9\s-]+$/;

    if (emailPattern.test(contact)) {
        // force email lowercase
        loginContact = contact.toLowerCase();
        console.log('[LoginOverlay] Normalized email:', loginContact);
    } else if (phonePattern.test(contact)) {
        // normalize phone to +E.164
        loginContact = normalizePhone(contact);
        console.log('[LoginOverlay] Normalized phone:', loginContact);
    }

    let loginResponse;
    try {
        loginResponse = await UserService.login(loginContact, password);
    } catch (err) {
        console.error('Login error:', err);
        errorMessage.value =
            err.response?.data?.error || 'An error occurred during login. Please try again.';
        loading.value = undefined;
        return;
    }

    const { UserProfile, Auth_Tokens } = loginResponse.data;
    auth_store.setAuthTokens(Auth_Tokens);
    auth_store.setProfile(UserProfile);
    isVisible.value = false;
    emit('update:modelValue', false);

    const redirectTo = localStorage.getItem('redirectAfterLogin') || '/post-feed-view';
    console.log('[LoginOverlay] emitting login-success');
    duellyEventEmitter.emit('login-success');
    reset();
    router.push(redirectTo);
}

// async function handleLogin() {
//     console.log('[LoginOverlay] handleLogin');
//     loading.value = '#FF5C39';
//     const { contact, password } = formData.value;
//     let loginResponse;
//     try {
//         loginResponse = await UserService.login(contact, password);
//     } catch (err) {
//         console.error('Login error:', err);
//         errorMessage.value =
//             err.response?.data?.error || 'An error occurred during login. Please try again.';
//         loading.value = undefined;
//         return;
//     }
//     const { UserProfile, Auth_Tokens } = loginResponse.data;
//     auth_store.setAuthTokens(Auth_Tokens);
//     auth_store.setProfile(UserProfile);
//     isVisible.value = false;
//     emit('update:modelValue', false);
//     const redirectTo = localStorage.getItem('redirectAfterLogin') || '/post-feed-view';
//     console.log('[LoginOverlay] emitting login-success');
//     duellyEventEmitter.emit('login-success');
//     reset();
//     router.push(redirectTo); // Redirect
// }

async function handleSignup() {
    loading.value = '#FF5C39';

    const { contact, password } = formData.value;
    let email = null;
    let phone_number = null;

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^\+?[0-9\s-]+$/;
    if (emailPattern.test(contact)) {
        email = contact.toLowerCase();
    } else {
        phone_number = normalizePhone(contact);
    }

    try {
        const res = await UserService.signUp(password, email, phone_number);
        const normalizedContact = phone_number || email;
        confirmationContact.value = normalizedContact;
        confirmationUserId.value = res.data.user_id;
        confirmationStep.value = true;
        localStorage.setItem('password', password);
    } catch (err) {
        console.error('Signup error:', err);
        errorMessage.value =
            err.response?.data?.error || 'An error occurred during signup. Please try again.';
    }
}

async function handleResetRequest() {
    loading.value = '#FF5C39';
    try {
        const res = await UserService.forgotPassword(resetContact.value);
        info_message.value = `A reset code was sent to ${resetContact.value}`;
        confirmationUserId.value = res.data.user_id; // save for confirm step
        showForgotPassword.value = 'code'; // step 2
    } catch (err) {
        errorMessage.value = err.response?.data?.error || 'Error sending reset code.';
    } finally {
        loading.value = undefined;
    }
}

async function handleResetConfirm() {
    if (newPassword.value !== confirmPassword.value) {
        errorMessage.value = 'Passwords do not match';
        return;
    }

    loading.value = '#FF5C39';
    try {
        await UserService.confirmResetPassword(
            confirmationUserId.value,
            resetCode.value,
            newPassword.value
        );
        info_message.value = 'Password reset successful. Please log in.';
        showForgotPassword.value = false;
        resetCode.value = '';
        newPassword.value = '';
        confirmPassword.value = '';
    } catch (err) {
        errorMessage.value = err.response?.data?.error || 'Error resetting password.';
    } finally {
        loading.value = undefined;
    }
}

const discordAuthUrl = computed(() => {
    return (
        process.env.VUE_APP_DISCORD_OAUTH2 ||
        'https://discord.com/oauth2/authorize?client_id=1358734731604136068&response_type=code&redirect_uri=https%3A%2F%2Fdev-pwa.duelly.net%2Fdiscord%2Fcallback&scope=email+identify'
    );
});

const handleDiscordLogin = () => {
    if (!discordAuthUrl.value) {
        console.error('Missing Discord OAuth2 URL in env');
        return;
    }
    window.location.href = discordAuthUrl.value;
};

const triggerGoogleLogin = () => {
    const clientId = '120650553600-s6e7dmkluio2mjfkl8f7sol7jtbqniea.apps.googleusercontent.com';
    const redirectUri = encodeURIComponent(`${window.location.origin}/google/callback`);

    const scope = encodeURIComponent('openid email profile');
    const nonce = encodeURIComponent(Math.random().toString(36).substring(2));

    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=id_token&scope=${scope}&nonce=${nonce}`;
    window.location.href = url;
};

const handleCodeInput = () => {
    if (confirmationCode.value.length === 6) {
        handleConfirmation();
    }
};

async function handleConfirmation() {
    loading.value = '#FF5C39';
    errorMessage.value = '';

    try {
        await UserService.confirmLogin(confirmationUserId.value, confirmationCode.value);
    } catch (error) {
        console.log('confirm error', error);
        errorMessage.value = error.response?.data?.error || 'Invalid code';
        loading.value = undefined;
        return;
    }

    // login
    try {
        const loginResponse = await UserService.login(
            confirmationContact.value,
            localStorage.getItem('password')
        );
        auth_store.setProfile(loginResponse.data.UserProfile);
        auth_store.setAuthTokens(loginResponse.data.Auth_Tokens);
        localStorage.removeItem('password');
        emit('update:modelValue', false);
        router.push('/post-feed-view');
    } catch (err) {
        errorMessage.value = err.response?.data?.error || 'Login Failed';
        loading.value = undefined;
    }
}

function normalizePhone(input) {
    console.log('[signup] Normalizing phone number:', input);
    if (!input) return '';

    // Strip out spaces, dashes, parentheses
    let phone = input.replace(/[\s()-]/g, '');
    console.log('[signup] Stripped phone:', phone);

    // If starts with +, keep as-is
    if (phone.startsWith('+')) {
        console.log('[signup] Phone number already normalized:', phone);
        return phone;
    }

    // If starts with 1 and length is 11, assume US
    if (phone.length === 11 && phone.startsWith('1')) {
        console.log('[signup] Adding + to phone number');
        return `+${phone}`;
    }

    // If length is 10, assume US and add +1
    if (phone.length === 10) {
        console.log('[signup] Adding +1 to phone number');
        return `+1${phone}`;
    }

    console.log('[signup] fallbacks:', phone);
    return phone; // fallback, may still fail validation
}

onMounted(() => {});
</script>

<style scoped lang="scss">
@use '@/assets/scss/abstracts/variables' as *;
@use '@/assets/scss/global.scss' as *;

.login-overlay {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: $bottom-nav-bar-size;
    min-height: calc(100vh - #{$bottom-nav-bar-size});

    @media (min-width: $desktop-max-width) {
        margin-left: $side-nav-width;
    }
}

.login-content {
    max-width: 600px;
    width: 100%;
    margin: auto;
    // border: 1px solid white;
}
.login-card {
    position: relative;
    padding-top: 18px; // enough to clear the X button
    border: 1px solid white;
}

.divider {
    position: relative;
    text-align: center;
    font-size: 16px;
    color: #aaa;
}

.divider:before,
.divider:after {
    content: '';
    position: absolute;
    top: 50%;
    width: 45%;
    height: 1px;
    background: #ddd;
}

.divider:before {
    left: 0;
}

.divider:after {
    right: 0;
}

#logo {
    background-image: url('@/assets/imgs/duelly_logo_transparent.png');
    background-size: contain;
    background-repeat: no-repeat;
    width: 100%;
    height: 200px;
    animation: logoTransition 5s ease-in-out forwards;
    margin-bottom: 20px;
}

@keyframes logoTransition {
    0% {
        background-image: url('@/assets/imgs/duelly_logo_transparent.png');
    }
    100% {
        background-image: url('@/assets/imgs/duelly_logo_glow_more.png');
    }
}
</style>
